
    import Vue from "vue";
    import Component from "vue-class-component";
    import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
    import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
    import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
    import {BModal, BvTableFieldArray} from "bootstrap-vue";
    import {Prop} from "vue-property-decorator";
    import {
        CreateAccessLinkRequest,
        factory,
        ResultError, ShareableResourceModel,
        SharedResourcesClient,
        AccessLinkModel
    } from "@/api-client";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import {formatDate} from "@/formatting/dateFormatter";
    import toasts from "@/components/Toasts";
    import {NavigationGuardNext} from "vue-router/types/router";

    interface Props {
        seafarerId: string;
        preLoadedSharedResources: AccessLinkModel[];
        resourceOptions: ShareableResourceModel[];
    }

    @Component({
        components: {
            SectionPageHeading,
            HeadingGroup,
            CheckboxInput,
            TableEmptySlot
        },
        beforeRouteEnter(to, from, next: NavigationGuardNext<SeafarerSharedResources>) {
            const props = to.meta!.props as Props;
            props.seafarerId = to.params.seafarerId;
            Promise.all([
                new SharedResourcesClient().list(to.params.seafarerId),
                new SharedResourcesClient().resourcesOptions()
            ]).then(values => {
                props.preLoadedSharedResources = values[0];
                props.resourceOptions = values[1];
                next();
            }).catch(e => next(e))
        }
    })
    export default class SeafarerSharedResources extends Vue implements Props {
        @Prop({required: true})
        seafarerId!: string;
        @Prop({required: true})
        preLoadedSharedResources!: AccessLinkModel[];
        @Prop({required: true})
        resourceOptions!: ShareableResourceModel[];

        sharedResources = this.preLoadedSharedResources;

        addModel = this.newAddModel();
        errors = factory.create(ResultError);
        revokeModel = factory.create(AccessLinkModel);
        detailsModel = factory.create(AccessLinkModel);

        $refs!: {
            addModal: BModal
            revokeModal: BModal
            detailsModal: BModal
        }

        async addLink() {
            this.errors = factory.create(ResultError);
            await new SharedResourcesClient()
                .add(this.addModel, this.seafarerId)
                .then(async () => {
                    this.$refs.addModal.hide();
                    this.sharedResources = await new SharedResourcesClient().list(this.seafarerId);
                    toasts.success(this.$ls.SharedResources_Toast_Success_LinkGenerated);
                })
                .catch(error => this.setValidationErrors(error, this.$ls.Global_Toast_FormAddFailure, "errors"));
        }

        openAddModal() {
            this.errors = factory.create(ResultError);
            this.addModel = this.newAddModel();
            this.$refs.addModal.show();
        }

        newAddModel(): CreateAccessLinkRequest {
            const model = factory.create(CreateAccessLinkRequest);
            model.sharedResources = [];
            return model;
        }

        openDetailsModal(link: AccessLinkModel) {
            this.detailsModel = link;
            this.$refs.detailsModal.show();
        }

        openRevokeModal(link: AccessLinkModel){
            this.revokeModel = link;
            this.$refs.revokeModal.show();
        }

        async revokeLink(){
            await new SharedResourcesClient().revoke(this.revokeModel.id, this.seafarerId)
                .then(async () => {
                    toasts.success(this.$ls.SharedResources_Toast_Success_LinkRemoved);
                    this.sharedResources = await new SharedResourcesClient().list(this.seafarerId);
                });
            this.$refs.revokeModal.hide();
        }

        getAccessTokenLink(token: string){
            return `${location.protocol}//${location.host}${this.$router.resolve({name: "person-profile", params: {seafarerId: this.seafarerId}, query: {accessLinkToken: token}}).href}`;
        }

        copyToClipboard(targetInput: string){
            const el = document.getElementById(targetInput) as HTMLInputElement;
            const isDisabled = el.disabled;
            if(isDisabled){
                el.disabled = false;
            }
            el.select();
            document.execCommand('copy');
            if(isDisabled){
                el.disabled = true;
            }
            toasts.success(this.$ls.Global_Toast_Success_CopiedToClipboard);
        }

        tableFields: BvTableFieldArray = [
            {
                key: 'validFrom',
                label: this.$ls.Global_Label_ValidFrom,
                thClass: 'w-15',
                formatter: (value: any, key: any, item: AccessLinkModel) => formatDate(item.validFrom)
            },
            {
                key: 'validUntil',
                thClass: 'w-15',
                label: this.$ls.Global_Label_ValidUntil,
                formatter: (value: any, key: any, item: AccessLinkModel) => formatDate(item.validUntil)
            },
            {
                key: 'sharedResources',
                label: this.$ls.SharedResources_Table_Label_SharedResources,
                formatter: (value: any, key: any, item: AccessLinkModel) => item.sharedResources.map(r => r.name).join(", ")
            },
            {
                key: 'isActive',
                label: this.$ls.Global_ValidityState_Valid,
                sortByFormatted: true,
                sortable: true,
                thClass: 'w-10',
                formatter: (value: any, key: any, item: AccessLinkModel) => item.isValid ? this.$ls.Global_Text_Yes : this.$ls.Global_Text_No
            },
            {
                key: 'actions',
                label: "",
                thClass: 'w-30',
                tdClass: 'table-actions'
            },
        ]
    }
